import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  DxTradeAccountPosition,
  DxTradeOpenPositionsResponse,
  DxTradePosition,
} from '@model/DxTrade.types'

import { filterOpenPositions } from '../utils/filters'
import { getPortfolioStoreSnapshot, subscribeToPortfolioStore } from './useDxtradeAccountWebSocket'

export const useDxtradeOpenPositions = (
  accountCode: string,
  query?: PageQuery
): {
  data: DxTradeOpenPositionsResponse
  isLoading: boolean
  forceRefresh: (skipLoadingFlag?: boolean) => void
} => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const filters = useMemo(
    () =>
      query
        ? { side: query.side, openTimeFrom: query.openTimeFrom, openTimeTo: query.openTimeTo }
        : undefined,
    [query?.side, query?.openTimeFrom, query?.openTimeTo]
  )

  const subscribe = useCallback(
    (listener: () => void) => subscribeToPortfolioStore(accountCode, listener),
    [accountCode]
  )

  const wsPortfolio = useSyncExternalStore(subscribe, () => getPortfolioStoreSnapshot(accountCode))

  const { data: restData, isLoading: restIsLoading } = useQuery({
    queryKey: queryKeys.trade.dxtrade.openPositions(accountCode),
    queryFn: () => apiClient.getDxtradeOpenPositions(accountCode),
    enabled: !!accountCode,
  })

  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.trade.dxtrade.openPositions(accountCode),
    })
  }, [queryClient, accountCode])

  const isValidWsPosition = (
    position: DxTradeAccountPosition
  ): position is DxTradeAccountPosition & DxTradePosition => {
    return (
      position.positionCode != null &&
      position.symbol != null &&
      position.account != null &&
      position.side != null
    )
  }

  const normalizeWsPositions = (positions: DxTradeAccountPosition[]) => {
    return positions.filter(isValidWsPosition)
  }

  const data = useMemo<DxTradeOpenPositionsResponse>(() => {
    const basePositions = wsPortfolio
      ? normalizeWsPositions(wsPortfolio.positions)
      : restData?.positions || []

    if (wsPortfolio) {
      return { positions: filterOpenPositions(basePositions, filters) }
    }
    return { positions: filterOpenPositions(basePositions, filters) }
  }, [wsPortfolio, restData, filters])

  const isLoading = wsPortfolio ? false : restIsLoading

  return { data, isLoading, forceRefresh }
}
